import React, { useEffect, useState } from "react";
import { Spin, Table, Tag } from "antd";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import SidebarNav from "../sidebar";
import { Link, useHistory } from "react-router-dom";
import backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import { CiEdit } from "react-icons/ci";
import { deletCoinNetworkById, getCoinNetwork } from "../../../service/api";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import moment from "moment";
import { MdDelete } from "react-icons/md";
import CoinStatusIndicator from "../commoncomponent/CoinStatusIndicator";
import { sanitizeInput } from "../../../config/validation";
import { triggerDownload } from "../../../config/downloadUtils";

const styleDelete = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 290,
  background: "white",
  // border: '1px solid #000',
  borderRadius: "10px",
  boxShadow: 24,
  p: 4,
  outline: "none",
};

const formatDate = (val) => (val ? moment(val).format("DD/MM/YYYY") : "-");

const ALL_COLUMNS = [
  {
    key: "coin",
    title: "Coin",
    defaultVisible: true,
    render: (row) => (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {row?.coin?.image && (
          <img
            src={row.coin.image}
            alt=""
            style={{ width: 24, height: 24, borderRadius: 12 }}
          />
        )}
        <div>
          <b>{row?.coin?.symbol || "—"}</b>
          <div style={{ fontSize: 11, opacity: 0.6 }}>
            {row?.coin?.name || ""}
          </div>
        </div>
      </div>
    ),
  },
  {
    key: "networks",
    title: "Networks",
    defaultVisible: true,
    render: (row) => {
      const list = Array.isArray(row?.networks) ? row.networks : [];
      if (list.length === 0)
        return <span style={{ opacity: 0.5 }}>—</span>;
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {list.map((n, i) => (
            <Tag key={n?.networkKey || i}>
              {n?.chainName || n?.networkKey}
            </Tag>
          ))}
        </div>
      );
    },
  },
  {
    key: "depositEnabled",
    title: "Deposit",
    defaultVisible: true,
    render: (row) => (
      <CoinStatusIndicator
        coin={row?.coin || {}}
        networks={row?.networks || []}
        flow="deposit"
      />
    ),
  },
  {
    key: "withdrawEnabled",
    title: "Withdraw",
    defaultVisible: true,
    render: (row) => (
      <CoinStatusIndicator
        coin={row?.coin || {}}
        networks={row?.networks || []}
        flow="withdraw"
      />
    ),
  },
  {
    key: "createdAt",
    title: "Date",
    defaultVisible: false,
    render: (row) => formatDate(row?.createdAt),
  },
];

const CoinNetwork = () => {
  const history = useHistory();
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const handleOpenDelete = () => setOpenDelete(true);
  const handleCloseDelete = () => setOpenDelete(false);
  const [id, setId] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "coin-network-list",
    allColumns: ALL_COLUMNS,
  });

  const getOrderList = () => {
    let body = {
      page: page,
      sizePerPage: pageSize,
      pagination: true,
    };
    if (searchTerm) {
      body.search = searchTerm;
    }

    setLoading(true);
    getCoinNetwork(body)
      .then((res) => {
        if (res?.status) {
          setOrderList(res?.data);
        }
        setLoading(false);
      })
      .finally((res) => {
        setLoading(false);
      });
  };

  const handleDownload = (format) => {
    const body = { pagination: false };
    if (searchTerm) body.search = searchTerm;
    setDownloadLoading(true);
    getCoinNetwork(body)
      .then((res) => {
        const result = res?.data?.result ?? [];
        if (!result.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
        const cols = ALL_COLUMNS.filter((c) => visibleColumnKeys.includes(c.key));
        triggerDownload(format, result, cols, "coin-networks");
      })
      .finally(() => setDownloadLoading(false));
  };

  useEffect(() => {
    getOrderList();
  }, [page, pageSize, searchTerm]);

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm]);

  const askDelete = (rowId) => {
    setId(rowId);
    handleOpenDelete();
  };

  const handleDeleteFunction = () => {
    if (!id) return;
    setLoading(true);
    deletCoinNetworkById(id)
      .then((res) => {
        if (res?.status === false) {
          toast.error(res?.message || "Delete failed", { toastId: 'coin-network-delete-error' });
          return;
        }
        toast.success(res?.message || "Coin network deleted", { toastId: 'coin-network-delete-success' });
        handleCloseDelete();
        setId(false);
        getOrderList();
      })
      .catch((e) => {
        toast.error(
          e?.response?.data?.message || e?.message || "Delete failed",
          { toastId: 'coin-network-delete-error' },
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const actionColumn = {
    key: "__action",
    title: "Action",
    className: "text-end",
    render: (_, row) => (
      <div className="text-end" style={{ gap: "10px" }}>
        <a
          onClick={() => {
            history.push(`/admin/network/edit/${row?._id}`);
          }}
          className="me-1 btn btn-sm bg-primary-light"
        >
          <CiEdit />
        </a>
        <button
          onClick={() => askDelete(row?._id)}
          disabled={loading}
          className="me-1 btn btn-sm bg-danger-light"
        >
          <MdDelete />
        </button>
      </div>
    ),
  };

  const columns2 = [...dynamicColumns, actionColumn];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-7 col-aut0">
                <h3 className="page-title">Coin Networks</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Coin Network </li>
                </ul>
              </div>
              <div className="col-sm-5 col">
                <a
                  onClick={() => {
                    history.push("/admin/network/new");
                  }}
                  data-bs-toggle="modal"
                  className="btn btn-primary float-end mt-2"
                >
                  Add New
                </a>
              </div>
            </div>
          </div>

          {/* /Page Header */}
          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card">
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={orderList?.total}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                    downloadLoading={downloadLoading}
                  />
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin">
                        <Spin
                          size="large"
                          style={{ position: "relative", top: "35%" }}
                        />
                      </div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        columns={columns2}
                        dataSource={
                          Array.isArray(orderList?.result)
                            ? orderList.result
                            : Array.isArray(orderList?.coins)
                              ? orderList.coins
                              : []
                        }
                        rowKey={(record) => record._id}
                      />
                    )}
                    {orderList && orderList?.total > pageSize && (
                      <CustomPagination
                        key={`pg-${pageSize}`}
                        totalTabs={orderList?.total}
                        tabsPerPage={pageSize}
                        handleChange={setPage}
                        className={`${
                          loading ? "display-hide" : "display-flex"
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="list-layout__filters">
              <FilterPanel
                count={orderList?.total}
                onClear={() => {
                  setSearchTerm("");
                }}
              >
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Coin or network name"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeInput(e.target.value))
                    }
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={openDelete}
            onClose={handleCloseDelete}
            closeAfterTransition
            slots={{ backdrop: backdrop }}
            slotProps={{
              backdrop: {
                timeout: 500,
              },
            }}
          >
            <Fade in={openDelete}>
              <Box style={styleDelete}>
                <div style={{ margin: "10px", textAlign: "center" }}>
                  Please confirm before deleting
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <button
                    className="btn btn-danger"
                    disabled={loading}
                    onClick={handleDeleteFunction}
                    style={{ marginBottom: "10px", marginTop: "15px" }}
                  >
                    {loading ? <Spinner size="sm" /> : "Delete"}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCloseDelete}
                    style={{
                      marginLeft: "7px",
                      marginBottom: "10px",
                      marginTop: "15px",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Box>
            </Fade>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default CoinNetwork;
